import { Icon } from '@design-system';
import { getInitials } from '@utils/strings';
import classNames from 'classnames';
import Link from 'next/link';
import React from 'react';

import { Image } from '../Image';

export type Option = {
  id: string;
  description?: string;
  thumbnail?: string | null;
  href?: string;
  title: string;
  variant: 'artist' | 'add-new';
};

export type SearchResultsProps = {
  bordered: boolean;
  options: Option[];
  onSelect: (option: Option, position: number) => void;
  query?: string;
  cursor: number;
};

const Thumbnail = ({ thumbnail, title, variant }: Partial<Option>) => {
  let icon = <Icon name="ic_location" />;
  if (variant === 'add-new') icon = <Icon name="ic_add" />;
  if (variant === 'artist' && title) {
    icon = <span>{getInitials(title, 2)}</span>;
  }
  return (
    <div
      className={classNames(
        'flex h-11 w-11 shrink-0 items-center justify-center',
        'overflow-hidden bg-neutral-1 fill-neutral-4',
        variant === 'add-new' ? 'rounded-full' : 'rounded-md',
      )}
    >
      {thumbnail ? (
        <Image alt={title || ''} src={thumbnail} width={64} />
      ) : (
        icon
      )}
    </div>
  );
};

export const Option = (props: { option: Option }) => {
  const { option } = props;
  return (
    <>
      <Thumbnail
        thumbnail={option.thumbnail}
        title={option.title}
        variant={option.variant}
      />
      <div className="flex min-w-0 flex-col transition-colors duration-300 group-hover:text-neutral-4">
        <div className="truncate">{option.title}</div>
        {option.description && (
          <div className="truncate text-neutral-4">{option.description}</div>
        )}
      </div>
    </>
  );
};

export const OptionButton = ({
  cursor,
  index,
  onSelect,
  option,
}: {
  option: Option;
  cursor: number;
  index: number;
  onSelect: (option: Option, position: number) => void;
}) => {
  return (
    <button
      className={classNames('group flex items-center gap-3', {
        'rounded-md bg-neutral-1': cursor === index,
      })}
      onClick={() => onSelect(option, index)}
      type="button"
    >
      <Option option={option} />
    </button>
  );
};

export const SearchResults = ({
  bordered,
  cursor,
  onSelect,
  options = [],
  query,
}: SearchResultsProps) => {
  if (options.length === 0) {
    return null;
  }

  return (
    <div
      className={classNames(
        'rounded-sm bg-white shadow-card',
        'flex flex-col gap-3 p-4',
        {
          'boxShadow-card border-none': !bordered,
          'border border-divider/20': bordered,
        },
      )}
    >
      {options.map((option, index) =>
        option.href ? (
          <Link
            key={`${option.id}-${option.href}`}
            className={classNames('group flex items-center gap-3', {
              'rounded-md bg-neutral-1': cursor === index,
            })}
            href={option.href}
            onClick={() => onSelect(option, index)}
          >
            <Option option={option} />
          </Link>
        ) : (
          <OptionButton
            cursor={cursor}
            index={index}
            onSelect={onSelect}
            option={option}
          />
        ),
      )}
      {query ? (
        <OptionButton
          cursor={cursor}
          index={options.length}
          onSelect={onSelect}
          option={{ id: query, title: query, variant: 'add-new' }}
        />
      ) : null}
    </div>
  );
};
